import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Filter, Users, X, Download } from 'lucide-react';
import ReportService from '../services/ReportService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';
import Alert from '../components/ui/Alert';
import Loader from '../components/ui/Loader';
import AttendanceCalendar from '../components/attendance/AttendanceCalendar';
import AttendanceSummary from '../components/attendance/AttendanceSummary';
import MonthPicker from '../components/attendance/MonthPicker';
import CalendarManagement from '../components/attendance/CalendarManagement';
import { useToast } from '../contexts/ToastContext';

const Attendance = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const { success, error } = useToast();

  const fetchCalendarEvents = useCallback(async (month, classId) => {
    if (!month) return;
    try {
      const { startDate, endDate } = getMonthStartEnd(month);
      const apiRequest = new APIRequest();
      const response = await apiRequest.request(
        `/admin/calendar/get-events?startDate=${startDate}&endDate=${endDate}&classId=${classId || ''}`,
        'GET',
        null,
        user?.token
      );
      if (response.success) {
        setCalendarEvents(response.data);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    }
  }, [user?.token]);

  const fetchClasses = useCallback(async () => {
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const response = await apiRequest.request("/admin/getAllClasses", "GET", null, user?.token);
      if (response.success) {
        setClasses(response.classes || []);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setAlert({ type: 'error', message: 'Failed to fetch classes' });
    } finally {
      setApiLoading(false);
    }
  }, [user?.token]);

  const fetchClassData = useCallback(async (classId) => {
    if (!classId) return;
    
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const response = await apiRequest.request(`/admin/getClass?id=${classId}`, 'GET', {}, user?.token);
      if (response.success) {
        setClassData(response.class);
        setStudents(response.class.enrolledStudents || []);
      } else {
        setAlert({ type: 'error', message: response.message || 'Failed to fetch class details' });
      }
    } catch (err) {
      console.error('Error fetching class data:', err);
      setAlert({ type: 'error', message: 'Failed to fetch class details' });
    } finally {
      setApiLoading(false);
    }
  }, [user?.token]);

  const fetchMonthlyAttendance = useCallback(async (classId, month) => {
    if (!classId || !month) return;
    
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const { startDate } = getMonthStartEnd(month);
      const payload = {
        class_id: classId,
        date: startDate.toString(),
      };
      const response = await apiRequest.request(`/admin/getAttendanceByClassAndDate`, 'POST', payload,  user?.token);
      
      if (response.success) {
        // Transform the data to match our expected format
        const attendanceRecord = response.data;
        if (attendanceRecord && attendanceRecord.students) {
          // Calculate holidays to exclude from summary (Same normalization as server)
          const holidayDateKeys = new Set(calendarEvents
            .filter(e => e.type === 'holiday' || e.type === 'school-leave')
            .map(e => {
                const d = new Date(Number(e.date) + 43200000);
                return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
            })
          );

          const studentSummary = attendanceRecord.students.map(student => {
            const nonHolidayAttendance = student.attendance.filter(a => {
                const d = new Date(Number(a.date) + 43200000);
                const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
                return !holidayDateKeys.has(key);
            });

            return {
                studentId: student.id,
                totalDays: nonHolidayAttendance.length,
                presentDays: nonHolidayAttendance.filter(a => a.action === 'present').length,
                absentDays: nonHolidayAttendance.filter(a => a.action === 'absent').length,
                halfDays: nonHolidayAttendance.filter(a => a.action === 'half').length,
                attendance: student.attendance
            };
          });
          
          setAttendanceData({
            studentSummary: studentSummary,
            attendanceByDate: {},
            totalStudents: studentSummary.length
          });
        } else {
          // No attendance data, but we still want to show the summary with correct total days
          const daysInMonth = getDaysInMonth(selectedMonth);
          const studentSummary = students.map(student => ({
            studentId: student._id,
            totalDays: daysInMonth,
            presentDays: 0,
            absentDays: 0,
            halfDays: 0,
            attendance: []
          }));
          
          setAttendanceData({
            studentSummary: studentSummary,
            attendanceByDate: {},
            totalStudents: studentSummary.length
          });
        }
      } else {
        setAttendanceData(null);
      }
    } catch (err) {
      console.error('Error fetching monthly attendance:', err);
      setAlert({ type: 'error', message: 'Failed to fetch attendance data' });
      setAttendanceData(null);
    } finally {
      setApiLoading(false);
    }
  }, [user?.token, selectedMonth, students]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) {
      fetchClassData(selectedClass);
    }
  }, [selectedClass, fetchClassData]);

  useEffect(() => {
    if (selectedClass && selectedMonth) {
      fetchMonthlyAttendance(selectedClass, selectedMonth);
    }
  }, [selectedClass, selectedMonth, fetchMonthlyAttendance]);

  useEffect(() => {
    if (selectedMonth) {
      fetchCalendarEvents(selectedMonth, selectedClass);
    }
  }, [selectedMonth, selectedClass, fetchCalendarEvents]);


  // Get unique academic years from classes
  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        academicYears.add(`${fromYear}-${toYear}`);
      }
    });
    return Array.from(academicYears).sort();
  };

  // Get classes filtered by selected academic year
  const getFilteredClasses = () => {
    if (!selectedAcademicYear) return [];
    
    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const academicYear = `${fromYear}-${toYear}`;
      return academicYear === selectedAcademicYear;
    });
  };

  const handleAcademicYearChange = (e) => {
    setSelectedAcademicYear(e.target.value);
    setSelectedClass(''); // Reset class selection when academic year changes
    setClassData(null);
    setStudents([]);
    setAttendanceData(null);
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
  };

  const handleClearFilters = () => {
    setSelectedAcademicYear('');
    setSelectedClass('');
    setSelectedMonth(new Date().toISOString().slice(0, 7));
    setClassData(null);
    setStudents([]);
    setAttendanceData(null);
    setAlert(null);
  };

  // Helper function to get month start and end dates
  const getMonthStartEnd = (month) => {
    const [year, monthNum] = month.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1);
    const endDate = new Date(year, monthNum, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return {
      startDate: startDate.getTime(),
      endDate: endDate.getTime()
    };
  };

  // Get days in month
  const getDaysInMonth = (month) => {
    const [year, monthNum] = month.split('-').map(Number);
    return new Date(year, monthNum, 0).getDate();
  };

  // Get attendance status for a specific student and date
  const getStudentAttendanceForDate = (studentId, date) => {
    if (!attendanceData?.studentSummary) return null;
    
    const studentSummary = attendanceData?.studentSummary?.find(s => 
      (s.studentId?.toString() || s.id?.toString()) === studentId?.toString()
    );
    if (!studentSummary) return null;

    // Use a robust UTC date comparison (with 12h leap) to avoid timezone/midnight shifting issues
    const target = new Date(Number(date) + 43200000);
    const targetKey = `${target.getUTCFullYear()}-${target.getUTCMonth()}-${target.getUTCDate()}`;
    
    const attendanceEntry = studentSummary?.attendance?.find(a => {
      const entryDate = new Date(Number(a?.date) + 43200000);
      const entryKey = `${entryDate.getUTCFullYear()}-${entryDate.getUTCMonth()}-${entryDate.getUTCDate()}`;
      return entryKey === targetKey;
    });
   
    return attendanceEntry ? attendanceEntry?.action : null;
  };

  // Update student attendance for multiple dates
  const updateStudentAttendance = async (updates) => {
    if (!selectedClass || !updates || updates.length === 0) return;
    
    setApiLoading(true);
    try {
      const apiRequest = new APIRequest();
      const { startDate, endDate } = getMonthStartEnd(selectedMonth);
      
      // Group updates by student
      const studentUpdates = {};
      updates.forEach(update => {
        if (!studentUpdates[update.studentId]) {
          studentUpdates[update.studentId] = [];
        }
        // Ensure date is a string of the timestamp
        studentUpdates[update.studentId].push({
          date: update.date.toString(),
          action: update.status
        });
      });

      // Convert to API format
      const students = Object.entries(studentUpdates).map(([studentId, attendance]) => ({
        id: studentId,
        attendance: attendance
      }));

      const attendanceData = {
        class_id: selectedClass,
        startDate: startDate.toString(),
        endDate: endDate.toString(),
        students: students
      };

      const response = await apiRequest.request("/admin/bulkUpdateAttendance", "POST", attendanceData, user?.token);
      
      if (response.success) {
        const actionCounts = updates.reduce((acc, update) => {
          acc[update.status] = (acc[update.status] || 0) + 1;
          return acc;
        }, {});
        
        const actionText = Object.entries(actionCounts)
          .map(([action, count]) => `${count} ${action}`)
          .join(', ');
        
        success(`Successfully updated ${updates.length} attendance record(s): ${actionText}`);
        // Refresh attendance data
        fetchMonthlyAttendance(selectedClass, selectedMonth);
      } else {
        error(response.message || 'Failed to update attendance');
      }
    } catch (err) {
      console.error('Error updating attendance:', err);
      error('Failed to update attendance');
    } finally {
      setApiLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    if (selectedClass && selectedMonth) {
      fetchMonthlyAttendance(selectedClass, selectedMonth);
      fetchCalendarEvents(selectedMonth, selectedClass);
    }
  }, [selectedClass, selectedMonth, fetchMonthlyAttendance, fetchCalendarEvents]);

  // Get attendance color for calendar cell
  const getAttendanceColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-500';
      case 'absent':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto relative select-none">
      {/* Overlay Loader */}
      {apiLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <Loader />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      )}


      {/* Modern Header Section */}
      <div className="glass-card rounded-[2.5rem] p-8 px-10 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              Student Attendance
            </span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
            <Calendar size={18} className="text-[#1B6CA8]" />
            Manage and track monthly student attendance
          </p>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {/* Filters */}
      <div className="glass-card rounded-[2rem] p-8 premium-shadow border-none animate-slideInUp relative z-30">
        <div className="flex flex-col space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">Filters</h3>
              <p className="text-slate-400 text-sm font-medium">Select criteria to view attendance</p>
            </div>
            {(selectedAcademicYear || selectedClass) && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 font-bold rounded-2xl transition-all duration-300 group shadow-sm active:scale-95"
              >
                <X size={16} className="transition-transform group-hover:rotate-90" />
                <span className="text-xs uppercase tracking-widest">Clear Filters</span>
              </button>
            )}
          </div>
          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Academic Year Selection */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Academic Year</label>
              <select
                value={selectedAcademicYear}
                onChange={handleAcademicYearChange}
                disabled={apiLoading}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-bold text-slate-700 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed shadow-sm"
              >
                <option value="">{apiLoading ? 'Loading...' : 'Select year'}</option>
                {getUniqueAcademicYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Class Selection */}
            <div className="flex flex-col">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Class</label>
              <select
                value={selectedClass}
                onChange={handleClassChange}
                disabled={apiLoading || !selectedAcademicYear}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-bold text-slate-700 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed shadow-sm"
              >
                <option value="">
                  {!selectedAcademicYear 
                    ? 'Select academic year first' 
                    : apiLoading 
                      ? 'Loading classes...' 
                      : getFilteredClasses().length === 0
                        ? 'No classes found'
                        : 'Select a class'}
                </option>
                {getFilteredClasses().map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.name} - {cls.section}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Selection */}
            <div className="flex flex-col animate-fadeIn">
              <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Month</label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <MonthPicker
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    disabled={apiLoading}
                    minDate={classData ? (() => {
                      const from = classData.academicYearFrom;
                      if (!from) return null;
                      const date = from.toString().length <= 4 ? new Date(from, 0) : new Date(from);
                      return date.toISOString().slice(0, 7);
                    })() : null}
                    maxDate={classData ? (() => {
                      const to = classData.academicYearTo;
                      if (!to) return null;
                      const date = to.toString().length <= 4 ? new Date(to, 11) : new Date(to);
                      return date.toISOString().slice(0, 7);
                    })() : null}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Legend - Premium Styled */}
          <div className="flex flex-wrap items-center gap-6 text-[10px] font-black uppercase tracking-widest pt-4 border-t border-slate-50">
            <div className="flex items-center gap-2 group cursor-default">
              <div className="w-3 h-3 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/20 group-hover:scale-125 transition-transform"></div>
              <span className="text-slate-400 group-hover:text-emerald-600 transition-colors">Present</span>
            </div>
            <div className="flex items-center gap-2 group cursor-default">
              <div className="w-3 h-3 bg-rose-500 rounded-full shadow-lg shadow-rose-500/20 group-hover:scale-125 transition-transform"></div>
              <span className="text-slate-400 group-hover:text-rose-600 transition-colors">Absent</span>
            </div>
            <div className="flex items-center gap-2 group cursor-default">
              <div className="w-3 h-3 bg-slate-300 rounded-full shadow-sm group-hover:scale-125 transition-transform"></div>
              <span className="text-slate-400 group-hover:text-slate-600 transition-colors">No Entry</span>
            </div>
          </div>
        </div>
      </div>

      {/* Event Management - Visible when month is selected */}
      {selectedMonth && (
        <div className="relative z-10 animate-fadeIn">
          <CalendarManagement 
            selectedMonth={selectedMonth}
            classId={selectedClass}
            onEventChange={() => {
              fetchCalendarEvents(selectedMonth, selectedClass);
              if (selectedClass) {
                fetchMonthlyAttendance(selectedClass, selectedMonth);
              }
            }}
          />
        </div>
      )}

      {/* Class Info */}
      {classData && (
        <div className="glass-card rounded-[2.5rem] p-10 premium-shadow border-none animate-slideInUp bg-gradient-to-br from-white to-slate-50/50 relative z-20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-10">
            <div className="flex items-center gap-8">
              <div className="w-20 h-20 bg-blue-600 rounded-[2rem] flex items-center justify-center shadow-xl shadow-blue-600/20">
                <Users size={36} className="text-white" />
              </div>
              <div>
                <h3 className="text-3xl font-black text-slate-800 tracking-tight">
                  {classData.name} <span className="text-blue-600 mx-1">/</span> <span className="text-blue-600">{classData.section}</span>
                </h3>
                <div className="flex flex-wrap items-center gap-6 mt-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Class Teacher</p>
                      <p className="text-sm font-bold text-slate-700">{classData.classInchargeName}</p>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-slate-200 hidden sm:block"></div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Student Count</p>
                      <p className="text-sm font-bold text-slate-700">{students.length} Enrolled</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-6">
              <div className="flex bg-slate-100 rounded-2xl p-1.5 shadow-sm border border-slate-200">
                <button
                  onClick={() => {
                    if (!attendanceData?.studentSummary) return;
                    const days = generateCalendarDays();
                    const columns = [
                      { header: 'Student Name', dataKey: 'name', width: 45 },
                      ...days.map(d => ({ header: d.toString(), dataKey: `day_${d}`, width: 7 }))
                    ];
                    const data = attendanceData.studentSummary.map(s => {
                      const student = students.find(std => (std._id === s.studentId || std.id === s.studentId));
                      const row = { name: student?.name || 'N/A' };
                      days.forEach(d => {
                        const dayAttendance = s.attendance?.find(a => new Date(parseInt(a.date)).getDate() === d);
                        const status = dayAttendance?.action;
                        
                        // Check if it's a holiday for this day
                        const dayEvent = calendarEvents.find(e => new Date(e.date).getDate() === d);
                        const isHoliday = dayEvent?.type === 'holiday' || dayEvent?.type === 'school-leave';
                        
                        row[`day_${d}`] = status === 'present' ? 'P' : 
                                         status === 'absent' ? 'A' : 
                                         status === 'half' ? 'H' : 
                                         isHoliday ? 'H' : '-';
                      });
                      return row;
                    });
                    const monthName = new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
                    ReportService.generatePDF(
                      `Attendance - ${classData?.name || ''} ${classData?.section || ''} (${monthName})`,
                      columns,
                      data,
                      `attendance_${classData?.name}_${monthName}.pdf`,
                      'landscape'
                    );
                  }}
                  className="px-6 py-2.5 bg-white text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300 flex items-center gap-2 font-black text-xs uppercase tracking-widest shadow-sm"
                >
                  <Download size={14} />
                  <span>PDF Report</span>
                </button>
                <button
                  onClick={() => {
                    if (!attendanceData?.studentSummary) return;
                    const days = generateCalendarDays();
                    const data = attendanceData.studentSummary.map(s => {
                      const student = students.find(std => (std._id === s.studentId || std.id === s.studentId));
                      const row = { 'Student Name': student?.name || 'N/A' };
                      days.forEach(d => {
                        const dayAttendance = s.attendance?.find(a => new Date(parseInt(a.date)).getDate() === d);
                        const status = dayAttendance?.action;
                        
                        // Check if it's a holiday for this day
                        const dayEvent = calendarEvents.find(e => new Date(e.date).getDate() === d);
                        const isHoliday = dayEvent?.type === 'holiday' || dayEvent?.type === 'school-leave';
                        
                        row[d] = status === 'present' ? 'P' : 
                                status === 'absent' ? 'A' : 
                                status === 'half' ? 'H' : 
                                isHoliday ? 'H' : '-';
                      });
                      return row;
                    });
                    const monthName = new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' });
                    ReportService.generateExcel(data, `attendance_${classData?.name}_${monthName}.xlsx`, ['Student Name', ...days]);
                  }}
                  className="px-6 py-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all duration-300 flex items-center gap-2 font-black text-xs uppercase tracking-widest"
                >
                  <Download size={14} />
                  <span>Excel</span>
                </button>
              </div>
              
              <div className="flex items-center gap-6 px-6 py-4 bg-white rounded-3xl border border-slate-100 shadow-sm">
                <div>
                  <p className="text-xl font-black text-slate-900 leading-none mb-1">{classData.classId || 'N/A'}</p>
                  <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">Class ID</p>
                </div>
                <div className="w-px h-8 bg-slate-100"></div>
                <div className="text-right">
                  <p className="text-xl font-black text-slate-900 leading-none mb-1">{classData.class_capacity}</p>
                  <p className="text-[10px] font-bold text-teal-500 uppercase tracking-widest">Capacity</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Summary */}
      {selectedClass && (
        <div className="relative z-10 space-y-8">
          <AttendanceSummary
            attendanceData={attendanceData}
            students={students}
          />
        </div>
      )}

      {/* Attendance Calendar */}
      {selectedClass && classData ? (
        apiLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : (
          <div className="relative z-10">
            <AttendanceCalendar
              students={students}
              selectedMonth={selectedMonth}
              attendanceData={attendanceData}
              calendarEvents={calendarEvents}
              getStudentAttendanceForDate={getStudentAttendanceForDate}
              getAttendanceColor={getAttendanceColor}
              generateCalendarDays={generateCalendarDays}
              updateStudentAttendance={updateStudentAttendance}
              apiLoading={apiLoading}
              onRefresh={handleRefresh}
              minDate={(() => {
                const from = classData.academicYearFrom;
                if (!from) return null;
                const date = from.toString().length <= 4 ? new Date(from, 0) : new Date(from);
                return date.getTime();
              })()}
              maxDate={(() => {
                const to = classData.academicYearTo;
                if (!to) return null;
                const date = to.toString().length <= 4 ? new Date(to, 11, 31, 23, 59, 59) : new Date(to);
                return date.getTime();
              })()}
            />
          </div>
        )
      ) : (
        <div className="glass-card rounded-[2.5rem] p-16 premium-shadow border-none text-center animate-fadeIn">
          <div className="w-24 h-24 bg-blue-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 transition-transform hover:scale-110 duration-500 shadow-inner">
            <Calendar className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-3xl font-black text-slate-800 tracking-tight mb-4">Select a Class</h3>
          <p className="text-slate-500 max-w-md mx-auto font-medium text-lg leading-relaxed">
            Choose a class and month from the filters above to view and manage student attendance records.
          </p>
        </div>
      )}

    
    </div>
  );
};

export default Attendance;